import { useEffect, useRef } from "react";
import {
  commentMentionsCurrentUser,
  isSameParticipantSub,
  type WaveCommentRecord,
  type WaveParticipantRecord,
} from "@vida/wave-core";
import { showWaveNotification } from "../services/notifications";
import { trackActivity } from "../services/waveSession";

type Options = {
  waveTitle: string;
  comments: WaveCommentRecord[];
  mentionRoster: WaveParticipantRecord[];
  currentUserSub: string;
  enabled?: boolean;
};

/** Notify when a remote comment @mentions the current user (Yjs sync). */
export function useWaveCommentMentionNotifications({
  waveTitle,
  comments,
  mentionRoster,
  currentUserSub,
  enabled = true,
}: Options): void {
  const seenIdsRef = useRef<Set<string>>(new Set());
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    if (!bootstrappedRef.current) {
      for (const comment of comments) {
        seenIdsRef.current.add(comment.id);
      }
      bootstrappedRef.current = true;
      return;
    }

    for (const comment of comments) {
      if (seenIdsRef.current.has(comment.id)) continue;
      seenIdsRef.current.add(comment.id);

      if (isSameParticipantSub(comment.authorSub, currentUserSub)) continue;
      if (
        comment.authorSub === "agent:assistant" ||
        comment.authorSub.startsWith("agent:")
      ) {
        continue;
      }

      if (!commentMentionsCurrentUser(comment.body, currentUserSub, mentionRoster)) {
        continue;
      }

      const preview = comment.body.length > 140 ? `${comment.body.slice(0, 137)}…` : comment.body;
      const title = `Menzione in ${waveTitle}`;
      const detail = `${comment.authorName}: ${preview}`;
      showWaveNotification(title, detail);
      trackActivity("Commenti", `${comment.authorName} ti ha menzionato in un commento`);
    }
  }, [comments, currentUserSub, enabled, mentionRoster, waveTitle]);
}
